import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import Popper from '@mui/material/Popper';
import { useTheme, styled } from '@mui/material/styles';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import Typography from '@mui/material/Typography';

const LISTBOX_PADDING = 8; // px

import { lighten, darken } from '@mui/system';

export const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.95)
      : darken(theme.palette.primary.main, 0.8),
}));

export const GroupItems = styled('li')({
  padding: 0,
  whiteSpace: 'nowrap',
});

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <GroupHeader key={dataSet.key + index} sx={{ ...inlineStyle }}>
        <Typography sx={{ pl: 1 }}>{dataSet.group}</Typography>
      </GroupHeader>
    );
  }

  return (
    <GroupItems component="li" {...dataSet[0]} style={inlineStyle}>
      {dataSet[1]['label']}
    </GroupItems>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactChild[] = [];
  (children as React.ReactChild[]).forEach(
    (item: React.ReactChild & { children?: React.ReactChild[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 36;

  const getGroupHeight = (item: string) => {
    const rows = Math.floor(item.length / 45);
    const initialHeight = 36;
    const extraRowHeight = 18;

    return initialHeight + extraRowHeight * rows;
  };

  const getChildSize = (child: any) => {
    if (child.hasOwnProperty('group')) {
      return getGroupHeight(child.group);
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: '0',
      margin: '0',
    },
  },
});

const filterOptions = (options, { inputValue }) => {
  if (inputValue === '') {
    return options;
  }

  const filtered = options.filter((option) => {
    const itemMatch = option.label
      .toLowerCase()
      .includes(inputValue.toLowerCase());
    const groupMatch = option.dataset_name
      .toLowerCase()
      .includes(inputValue.toLowerCase());
    return itemMatch || groupMatch;
  });

  return filtered;
};

export const OptimizedAutocomplete: React.FC<any> = ({
  options,
  value,
  onChange,
  label,
  sx,
}) => {
  return (
    <Autocomplete
      id="virtualize-demo"
      value={value}
      onChange={onChange}
      sx={sx}
      fullWidth
      disableListWrap
      size="small"
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      options={options}
      filterOptions={filterOptions}
      // @ts-ignore
      groupBy={(option) => option.dataset_name}
      renderInput={(params) => <TextField {...params} label={label} />}
      renderOption={(props, option, state) =>
        [props, option, state.index] as React.ReactNode
      }
      renderGroup={(params) => params as unknown as React.ReactNode}
    />
  );
};
